import React, { useEffect, useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { authService } from '@/services/authService';
import PageHeader from '@/components/PageHeader';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { Trash2, UserCog } from 'lucide-react';

interface BasicUser {
  id: number;
  email: string;
  name: string;
  role: string;
  created_at: string;
  mbti?: string | null;
}

export const ManageUsersPage: React.FC = () => {
  const { isCurator } = useAuthStore();
  const [users, setUsers] = useState<BasicUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [removingUserId, setRemovingUserId] = useState<number | null>(null);
  const [userToRemove, setUserToRemove] = useState<BasicUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await authService.listUsers();
      setUsers(data);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isCurator()) {
      fetchUsers();
    } else {
      setError('Access denied');
      setLoading(false);
    }
  }, [isCurator]);

  const handleRemoveStudent = async (user: BasicUser) => {
    if (user.role !== 'student') {
      return;
    }

    try {
      setRemovingUserId(user.id);
      setError(null);
      await authService.removeStudent(user.id);
      setUsers((previous) => previous.filter((entry) => entry.id !== user.id));
      setUserToRemove(null);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to remove student');
    } finally {
      setRemovingUserId(null);
    }
  };

  return (
    <div className="min-h-[100svh] w-full bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-100/30 dark:from-gray-900 dark:via-slate-800 dark:to-slate-900">
      <PageHeader
        title="Manage Users"
        subtitle="Manage all registered users"
        icon={UserCog}
        iconColor="text-rose-600 dark:text-rose-400"
        iconBgColor="bg-rose-100 dark:bg-rose-900/20"
        showBackButton={false}
      />
      

      <div className="w-full py-3 sm:pb-3 overflow-y-auto h-[100svh]"
        style={{
          paddingTop: 'var(--header-height, 80px)'
        }}>
        {loading ? (
          <div className="text-gray-600 dark:text-gray-400">Loading users...</div>
        ) : error ? (
          <div className="text-red-600 dark:text-red-400">{error}</div>
        ) : (
          <div className="bg-white/80 dark:bg-gray-800/90 backdrop-blur-sm shadow-xl border border-white/20 dark:border-gray-700/50">
            <div className="max-h-[70vh] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
              {users.map((u) => (
                <div key={u.id} className="flex items-center justify-between p-4 sm:p-5">
                  <div className="min-w-0">
                    <p className="text-sm sm:text-base font-medium text-gray-900 dark:text-white truncate">
                      {u.email}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                      {u.name}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    {u.role === 'student' && u.mbti ? (
                      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                        {u.mbti}
                      </span>
                    ) : null}
                    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                      {u.role}
                    </span>
                    {u.role === 'student' ? (
                      <button
                        type="button"
                        onClick={() => setUserToRemove(u)}
                        disabled={removingUserId === u.id}
                        className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {removingUserId === u.id ? 'Removing...' : 'Remove'}
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
              {users.length === 0 && (
                <div className="p-6 text-center text-gray-500 dark:text-gray-400">No users found.</div>
              )}
            </div>
          </div>
        )}
      </div>
      <ConfirmDialog
        isOpen={Boolean(userToRemove)}
        title="Remove Student"
        message={
          <>
            Remove student account <span className="font-medium">{userToRemove?.email}</span>? This action cannot be undone.
          </>
        }
        confirmLabel="Remove"
        loading={removingUserId === userToRemove?.id}
        variant="danger"
        onCancel={() => setUserToRemove(null)}
        onConfirm={() => {
          if (userToRemove) {
            handleRemoveStudent(userToRemove);
          }
        }}
      />
    </div>
  );
};

export default ManageUsersPage;


